<script setup>
import { inject, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  consumePendingHomeSectionScroll,
  registerHomeSectionEnsurer,
  useHomeSectionNav,
} from '@/composables/useHomeSectionNav.js';
import { getMinSectionForHash, normalizeHomeHash } from '@/utils/homeHash.js';
import Homepage1 from './homepage1/index.vue';
import Homepage2 from './homepage2/index.vue';
import Homepage3 from './homepage3/index.vue';
import Homepage4 from './homepage4/index.vue';
import Homepage5 from './homepage5/index.vue';
import Homepage6 from './homepage6/index.vue';
import HomepageFaq from './homepageFaq/index.vue';
import Homepage7 from './homepage7/index.vue';
import SectionNav from '@/components/layout/SectionNav.vue';
import HomePageBackground from './HomePageBackground.vue';

const SCROLL_HIDDEN_CLASS = 'scroll-hidden';
/** Hero 外：产品 → 飞轮 → 计算器 → 场景 → 版本 → FAQ → 评价 → 侧栏 */
const SECTION_COUNT = 8;

const { pendingHash } = useHomeSectionNav();
const pageLoading = inject('pageLoading', ref(false));
const showBelowFold = ref(false);
const mountedSections = ref(0);

let idleTimer = 0;
let sectionIdleId = 0;

function cancelSectionMount() {
  if (sectionIdleId && typeof cancelIdleCallback === 'function') cancelIdleCallback(sectionIdleId);
  if (idleTimer) clearTimeout(idleTimer);
  sectionIdleId = 0;
  idleTimer = 0;
}

function ensureSectionsForHash(hash) {
  const required = getMinSectionForHash(hash);
  if (required <= 0) return;
  showBelowFold.value = true;
  mountedSections.value = Math.max(mountedSections.value, required);
}

function mountRemainingSections() {
  if (mountedSections.value >= SECTION_COUNT) return;

  let step = mountedSections.value;
  const mountNext = () => {
    step += 1;
    mountedSections.value = step;
    if (step < SECTION_COUNT) {
      if (typeof requestIdleCallback === 'function') {
        sectionIdleId = requestIdleCallback(mountNext, { timeout: 150 });
      } else {
        idleTimer = window.setTimeout(mountNext, 100);
      }
      return;
    }
    requestAnimationFrame(() => ScrollTrigger.refresh());
  };

  if (typeof requestIdleCallback === 'function') {
    sectionIdleId = requestIdleCallback(mountNext, { timeout: 150 });
  } else {
    idleTimer = window.setTimeout(mountNext, 100);
  }
}

function bootstrapSections() {
  cancelSectionMount();
  showBelowFold.value = true;

  const hash = normalizeHomeHash(pendingHash.value);
  if (hash) {
    ensureSectionsForHash(hash);
    void consumePendingHomeSectionScroll().then(() => {
      if (mountedSections.value < SECTION_COUNT) {
        requestAnimationFrame(() => requestAnimationFrame(mountRemainingSections));
      }
    });
    return;
  }

  mountedSections.value = 1;
  requestAnimationFrame(() => {
    requestAnimationFrame(mountRemainingSections);
  });
}

registerHomeSectionEnsurer(ensureSectionsForHash);

onMounted(() => {
  document.documentElement.classList.add(SCROLL_HIDDEN_CLASS);
});

watch(pageLoading, (loading) => {
  if (loading) {
    cancelSectionMount();
    showBelowFold.value = false;
    mountedSections.value = 0;
    return;
  }

  bootstrapSections();
}, { immediate: true });

onBeforeUnmount(() => {
  registerHomeSectionEnsurer(null);
  document.documentElement.classList.remove(SCROLL_HIDDEN_CLASS);
  cancelSectionMount();
});
</script>

<template>
  <div class="relative overflow-x-clip">
    <HomePageBackground />
    <div class="relative z-[1]">
      <Homepage1 />
      <template v-if="showBelowFold">
        <Homepage2 v-if="mountedSections >= 1" />
        <Homepage3 v-if="mountedSections >= 2" />
        <Homepage4 v-if="mountedSections >= 3" />
        <Homepage5 v-if="mountedSections >= 4" />
        <Homepage6 v-if="mountedSections >= 5" />
        <HomepageFaq v-if="mountedSections >= 6" />
        <Homepage7 v-if="mountedSections >= 7" />
        <SectionNav v-if="mountedSections >= 8" />
      </template>
    </div>
  </div>
</template>
